import { Search } from "@carbon/react";
import React, { useState, useEffect, useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";

// Custom hook for debouncing
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

// --- GraphQL Query ---
const ALL_TOPICS_QUERY = gql`
    query AllTopics {
        allTopics {
            id
            name
            messages {
                id
            }
        }
    }
`;

// --- Helper Components ---

function TopicCard({ topic }) {
    return (
        <a className="group wrap block w-full text-left bg-white rounded-xl border-2 border-neutral-200 transition-all duration-100 hover:border-neutral-300" href={`/admin/topics/${topic.id}`}>
            <div className="p-4">
                <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-lg">
                        {topic.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-grow min-w-0">
                        <h3 className="font-bold text-neutral-800 truncate" title={topic.name}>
                            {topic.name}
                        </h3>
                        <p className="text-sm text-neutral-500">
                            {topic.messages.length} {topic.messages.length === 1 ? 'Nachricht' : 'Nachrichten'}
                        </p>
                    </div>
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-500 bg-opacity-20 text-neutral-800">
                        Thema
                    </span>
                </div>
            </div>
        </a>
    );
}


function NoTopicsFound() {
    return (
        <div className="w-full text-center py-20 bg-neutral-50 rounded-lg">
            <h3 className="text-xl font-medium text-neutral-800">
                Keine Themen gefunden
            </h3>
            <p className="mt-2 text-base text-neutral-500">
                Es gibt keine Themen, die deinen Suchkriterien entsprechen.
            </p>
        </div>
    );
}

// --- Main Component ---
export default function TopicManagement() {
    const [searchTerm, setSearchTerm] = useState("");

    // Debounce search term to avoid excessive API calls
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const { data, loading, error } = useQuery(ALL_TOPICS_QUERY, {
        // Add caching and fetch policy optimizations
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all'
    });

    // Filter topics based on search term
    const filteredTopics = data?.allTopics?.filter(topic =>
        topic.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    ) || [];

    return (
        <section className="w-full">
            <header className="mb-6 flex flex-col md:flex-row justify-between md:items-center gap-4">
                <h1 className="text-3xl font-bold text-neutral-800">
                    Themenverwaltung
                </h1>
                <Link
                    to="/admin/topics/create"
                    className="mainBtn inline-flex items-center justify-center sm:w-auto w-full text-white px-5 py-2"
                >
                    <svg className="mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    <span>
                        Thema erstellen
                    </span>
                </Link>
            </header>
            <div className="mb-8">
                <Search size="lg" className="w-full" placeholder="Nach Themen suchen..." labelText="Search topics" hideLabel value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <main>
                {loading && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {Array.from({ length: 8 }).map((_, index) => (
                        <div key={index} className="bg-neutral-200 animate-pulse rounded-lg h-24"></div>
                    ))}
                </div>}
                {error && <div className="text-center py-8 text-red-600">Error: {error.message}</div>}
                {data && (
                    filteredTopics.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {filteredTopics.map((topic) => (
                                <TopicCard key={topic.id} topic={topic} />
                            ))}
                        </div>
                    ) : (
                        <NoTopicsFound />
                    )
                )}
            </main>
        </section>
    );
}
